/**
 * @fileoverview Input/output schemas for distill-changes-v1.
 *
 * This agent takes the multi-hat editorial feedback produced by
 * story-feedback-v1 and distills it into a succinct but comprehensive list
 * of high-level, structural change recommendations for the full story.
 *
 * The output is designed to be consumed by story-modifier-v1 — its
 * `changeRequest` field is formatted so it can be passed directly as the
 * natural-language change request that story-modifier-v1 expects. The
 * structured `changes` array is for human review in the UI.
 *
 * Inputs kept deliberately narrow:
 *  - The feedback object (the primary material to distill).
 *  - The writtenPlot (structural plan that story-modifier-v1 will act on).
 *  - The existingStorylines (so recommendations can reference storylines
 *    by name and phase).
 *
 * The full prose is NOT passed in — the feedback already references the
 * text, and story-modifier-v1 itself is the place where prose/plot is
 * rewritten.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';

// =============================================================================
// FEEDBACK INPUT SHAPE
// =============================================================================

/**
 * Minimal, local mirror of StoredStoryFeedback. Duplicated here (rather than
 * imported from @/domains/story-planning/schemas) to keep the agent package
 * self-contained and independent of domain schemas.
 */
const HatFeedbackInputSchema = z.object({
  headline: z.string(),
  rating: z.number(),
  body: z.string(),
  strengths: z.array(z.string()).default([]),
  concerns: z.array(z.string()).default([]),
});

const FeedbackInputSchema = z.object({
  overallImpression: z.string(),
  yellowPressCritic: HatFeedbackInputSchema,
  developmentalEditor: HatFeedbackInputSchema,
  publisher: HatFeedbackInputSchema,
}).describe(
  'The multi-hat story feedback produced by story-feedback-v1 (yellow press '
  + 'critic, developmental editor, publisher). This is the primary material '
  + 'to distill.'
);

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

export const InputSchema = z.object({
  ...BaseFlowInputSchema.shape,
  ...AGENT_SCHEMA_OBJECTS.writtenPlot.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.existingStorylines.schema.shape,
  feedback: FeedbackInputSchema,
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * Scope categories are aligned with what story-modifier-v1 can actually act on:
 * structural plot, storylines, character arcs, characters, theme/stakes, pacing.
 * The distill agent uses these to signal where a change lands so the author
 * (or a downstream step) can reason about impact.
 */
export const DistilledChangeScopeSchema = z.enum([
  'structure',
  'storylines',
  'character_arcs',
  'characters',
  'theme',
  'stakes',
  'pacing',
  'premise',
]);

export const DistilledChangePrioritySchema = z.enum(['high', 'medium', 'low']);

const DistilledChangeSchema = z.object({
  title: z.string().describe(
    'A short, action-oriented label for the change (6-10 words). '
    + 'Reads like a commit message: "Sharpen the stakes in Act 2 midpoint".'
  ),
  scope: DistilledChangeScopeSchema.describe(
    'Which part of the narrative planning this change targets. Aligned with '
    + 'the dimensions story-modifier-v1 can actually modify.'
  ),
  priority: DistilledChangePrioritySchema.describe(
    '"high" = structural, story-defining; "medium" = significant craft '
    + 'improvement; "low" = polish-level. Use judgement — do not flatten '
    + 'everything into one bucket.'
  ),
  rationale: z.string().describe(
    'Why this change matters, grounded in the feedback. Reference which hat '
    + '(critic / editor / publisher) raised the concern, and paraphrase the '
    + 'specific observation. 1-3 sentences.'
  ),
  recommendation: z.string().describe(
    'The concrete structural/narrative change to make. Must be specific '
    + 'enough to act on — name storylines/characters/phases where relevant. '
    + 'Do NOT prescribe exact prose. 1-4 sentences.'
  ),
});

export type DistilledChange = z.infer<typeof DistilledChangeSchema>;

export const OutputSchema = z.object({
  summary: z.string().describe(
    'A 2-3 sentence neutral framing of the overall direction of the changes. '
    + 'Should read like an editor\'s cover note: what is the author being '
    + 'asked to do with this round of revisions?'
  ),
  changes: z.array(DistilledChangeSchema).min(1).describe(
    'The distilled list of recommended changes. Aim for 3-8 items — succinct '
    + 'but comprehensive. Merge overlapping notes from different hats into a '
    + 'single change. Drop duplicates, contradictions, and minor prose-level '
    + 'quibbles (those belong elsewhere). Order from highest to lowest '
    + 'priority.'
  ),
  changeRequest: z.string().describe(
    'A single natural-language change request, formatted as direct input for '
    + 'story-modifier-v1. Write it as a prose-style instruction to a collaborator '
    + 'who will rewrite the storylines, character arcs, and writtenPlot. '
    + 'Reference specific storylines, characters, and structural phases by name. '
    + 'It must cover every item in the `changes` array, in the same priority '
    + 'order, without any UI framing (no headings like "Priority 1"). '
    + '6-15 sentences. This string should be usable verbatim as the '
    + '`changeRequest` field of story-modifier-v1.'
  ),
});

export type Output = z.infer<typeof OutputSchema>;
